import { useEffect, useRef } from 'react';
import { useCall } from '@/contexts/call-context';
import { Globe, PhoneOff, Video } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useQuery } from '@tanstack/react-query';
import ringtoneUrl from '@assets/Client_is_Calling_1788273913136.mp3';
import { IncomingCallDialog } from '@/components/calls/IncomingCallDialog';

interface AudioSettings {
  audioNotificationsEnabled: boolean;
}

/**
 * Ring modal for CLIENT calls started from a public project page (task #77).
 * Distinct from IncomingCallModal because accepting doesn't connect here —
 * it opens the share page in a new tab where the call actually runs (the
 * client and the team share the same page during the call).
 */
export function ClientIncomingCallModal() {
  const { incomingClientCall, acceptClientCall, declineClientCall } = useCall();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const acceptRef = useRef<HTMLButtonElement | null>(null);

  const { data: audioSettings } = useQuery<AudioSettings>({
    queryKey: ['/api/user/audio-settings'],
    enabled: !!incomingClientCall,
  });
  const soundEnabled = audioSettings?.audioNotificationsEnabled ?? false;

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    if (incomingClientCall && soundEnabled) {
      a.loop = true;
      a.volume = 0.5;
      a.play().catch(() => undefined);
    } else {
      a.pause();
      a.currentTime = 0;
    }
    return () => {
      if (a) { a.pause(); a.currentTime = 0; }
    };
  }, [incomingClientCall, soundEnabled]);

  if (!incomingClientCall) return null;

  const call = incomingClientCall;
  const avatarLetter = (call.guestName.trim()[0] || '?').toUpperCase();

  return (
    <>
      <audio ref={audioRef} src={ringtoneUrl} preload="auto" />
      <IncomingCallDialog
        title={`Incoming client call from ${call.guestName}`}
        description="Choose Accept to open the client project call or Decline to reject it."
        initialFocusRef={acceptRef}
      >
      <div className="flex flex-col items-center gap-4">
        <div className="h-20 w-20 rounded-full flex items-center justify-center text-2xl font-bold text-white animate-pulse bg-indigo-500">
          {avatarLetter}
        </div>
        <div className="text-center">
          <div className="text-base font-semibold">{call.guestName}</div>
          <div className="text-xs text-muted-foreground mt-1 flex items-center justify-center gap-1">
            <Video className="h-3 w-3" />
            Client call from the project page
          </div>
          {(call.projectName || call.projectId) && (
            <div className="text-xs text-muted-foreground mt-2 flex items-center justify-center gap-1">
              <Globe className="h-3 w-3" />
              {call.projectName || call.projectId}
            </div>
          )}
        </div>
        <div className="text-[11px] text-muted-foreground text-center">
          Accepting opens the client&apos;s project page in a new tab — the call runs there.
        </div>
        <div className="flex gap-3 w-full mt-1">
          <Button
            variant="destructive"
            className="flex-1 gap-2"
            onClick={() => { void declineClientCall(); }}
          >
            <PhoneOff className="h-4 w-4" /> Decline
          </Button>
          <Button
            ref={acceptRef}
            className="flex-1 gap-2 bg-green-600 hover:bg-green-700 text-white"
            onClick={() => { acceptClientCall(); }}
          >
            <Video className="h-4 w-4" /> Accept
          </Button>
        </div>
      </div>
      </IncomingCallDialog>
    </>
  );
}
